// #359: a logged session as the slots of one model day, one round, for
// modelFromSource. One row per exercise: its heaviest set, once per work set.
// Keep free of React and any UI import.
import { blankSlot, type Draft, type IntensityKind, type SetRowDraft, type SlotDraft } from "./programEdit.js";
import { blankPlanDraft, planToDraft } from "./planEdit.js";
import type { Plan } from "./schemas/plan.js";
import type { UnitsPreference } from "./schemas/profile.js";
import type { Workout, WorkoutExerciseEntry, WorkoutSet } from "./schemas/workout.js";
import { fromCanonicalKg } from "./units.js";
import { exerciseTraits, type TraitsCatalog } from "./exerciseTraits.js";
import { catalogBySlug } from "./volumeByMuscle.js";

/** A set below this share of the heaviest set's load is a warm-up. By load,
 *  not by effort: not isApproachSet/EFFORT_APPROACH_RIR (effortScale.ts). */
export const WARM_UP_BELOW = 0.8;

export type WorkoutSlots = {
  slots: SlotDraft[];
  /** Sets left out as warm-up, over the whole session. */
  warmUps: number;
  /** Exercises whose load is an extra on the body, assisted ones included:
   *  ModelSource.bodyweight, so they go to history and the summary says so. */
  bodyweight: string[];
};

type LoadSet = Extract<WorkoutSet, { type: "weighted" | "bodyweight" }>;

const isLoadSet = (s: WorkoutSet): s is LoadSet => s.type === "weighted" || s.type === "bodyweight";

/** The load on the bar, or the extra on a bodyweight set. A drop set is its
 *  top round: the set itself, not its steps. */
const loadOf = (s: LoadSet): number => (s.type === "weighted" ? s.weight : (s.extra ?? 0));

const repsOf = (s: WorkoutSet): string => ("reps" in s && s.reps !== undefined ? String(s.reps) : "");

type RowWork = { kind: "countup" } | { kind: "countdown"; sec: number };

/** One set as a one-round row. */
const setRow = (kind: IntensityKind, reps: string, value: string, work?: RowWork): SetRowDraft => ({
  intensityKind: kind,
  ...(work ? { workKind: work.kind } : {}),
  cells: [
    {
      reps,
      intensityValue: value,
      ...(work?.kind === "countdown" ? { workDurationSec: String(work.sec) } : {}),
    },
  ],
  notesPerCell: [""],
});

/** The countdown cell's ceiling (numericFieldValidation, "seconds"). */
const MAX_BOUT_SEC = 3600;

/** #368: a set whose work is not reps of a load. Timed, cardio and a carry
 *  with a time keep it as a countdown bout (up to MAX_BOUT_SEC: a longer one
 *  stays count-up with no time target, never clamped); the rest count up. Its
 *  load, if any (a weighted plank, a sled), stays on the row. A count-up row
 *  with no logged reps is one bout (reps 1, no AMRAP: that is behaviour for
 *  the logger), never «0 reps». */
const timeRow = (s: WorkoutSet, unit: UnitsPreference): SetRowDraft => {
  const kg = "weight" in s && typeof s.weight === "number" && s.weight > 0 ? s.weight : 0;
  const kind: IntensityKind = kg > 0 ? "absolute" : "none";
  const value = kg > 0 ? String(fromCanonicalKg(kg, unit)) : "";
  const sec = "duration" in s ? s.duration : undefined;
  if (sec !== undefined && sec >= 1 && sec <= MAX_BOUT_SEC) return setRow(kind, "0", value, { kind: "countdown", sec });
  const reps = repsOf(s);
  return setRow(kind, reps || "1", value, { kind: "countup" });
};

/** The heaviest set; on a tie the one with most reps, then the first. */
const topSet = (sets: readonly LoadSet[]): LoadSet =>
  sets.reduce((a, b) => (loadOf(b) > loadOf(a) || (loadOf(b) === loadOf(a) && b.reps > a.reps) ? b : a));

type EntryRows = { rows: SetRowDraft[]; warmUps: number; bodyweight: boolean };

const entryRows = (entry: WorkoutExerciseEntry, unit: UnitsPreference, catalog?: TraitsCatalog): EntryRows => {
  const sets = entry.sets.filter(isLoadSet);
  // Timed, cardio, carries: a row the recognizer does not read, so no load.
  if (sets.length === 0)
    return { rows: entry.sets.map((s) => timeRow(s, unit)), warmUps: 0, bodyweight: false };
  const top = topSet(sets);
  const load = loadOf(top);
  // With no added load every set is a work set.
  const work = load > 0 ? sets.filter((s) => loadOf(s) >= load * WARM_UP_BELOW).length : sets.length;
  const reps = String(top.reps);
  // A load wins over the effort: a row has one axis (plannedToRowCell).
  const make =
    load !== 0
      ? () => setRow("absolute", reps, String(fromCanonicalKg(load, unit)))
      : top.rpe !== undefined
        ? () => setRow("rpe", reps, String(top.rpe))
        : top.rir !== undefined
          ? () => setRow("rir", reps, String(top.rir))
          : () => setRow("none", reps, "");
  // An extra on the body, assisted (negative) included, is never a share of
  // the MAX. The saved type is the shape of the data (a weight or an extra),
  // as when a session is reopened (hydrateFromWorkout): it wins over the row.
  const row = catalogBySlug(catalog ?? []).get(entry.exerciseId);
  const bodyweight =
    load !== 0 && exerciseTraits(row, top.type, { preferStored: true }).isBodyweight;
  return { rows: Array.from({ length: work }, make), warmUps: sets.length - work, bodyweight };
};

/** The session's entries as slots, loads in `unit`. */
export const workoutToSlotDrafts = (
  workout: Pick<Workout, "entries">,
  unit: UnitsPreference,
  catalog?: TraitsCatalog,
): WorkoutSlots => {
  let warmUps = 0;
  const bodyweight: string[] = [];
  const slots = workout.entries.map((entry) => {
    const out = entryRows(entry, unit, catalog);
    warmUps += out.warmUps;
    if (out.bodyweight) bodyweight.push(entry.exerciseId);
    // #368: a superset or circuit stays one (plans; a model reads rows alone).
    const group = entry.group
      ? { id: entry.group.id, kind: entry.group.kind, ...(entry.group.name ? { name: entry.group.name } : {}) }
      : null;
    return { ...blankSlot(1), exerciseId: entry.exerciseId, exerciseName: entry.exerciseName, setRows: out.rows, group };
  });
  return { slots, warmUps, bodyweight };
};

/** #368: a new plan's first draft, and the unit its load cells are written
 *  in: the editor reads and saves them in that unit (its cellUnit).
 *  #374: `scheduledFor`, the day it opens on ("YYYY-MM-DD").
 *  #375: `source`, what it was made from ("session:<id>", "plan:<id>"): the
 *  device draft is kept under it until the first save, so reopening the same
 *  source finds it. */
export type PlanSeed = { draft: Draft; unit: UnitsPreference; scheduledFor?: string; source?: string };

/** #368: a logged session as a new plan («Plan again», the diary's «Plan»):
 *  its name, and its exercises as workoutToSlotDrafts reads them. */
export const workoutToPlanSeed = (
  workout: Pick<Workout, "entries" | "name"> & { id?: string },
  unit: UnitsPreference,
): PlanSeed => {
  const blank = blankPlanDraft();
  const name = workout.name ?? "";
  // No catalog: it only tells the bodyweight rows apart, which a plan does not.
  const { slots } = workoutToSlotDrafts(workout, unit);
  return {
    draft: { ...blank, name, sessions: blank.sessions.map((s) => ({ ...s, name, slots })) },
    unit,
    ...(workout.id ? { source: `session:${workout.id}` } : {}),
  };
};

/** #374: a plan as a new plan («Plan again» on one not done), no date. It
 *  belongs to whoever makes it: no assignees, a coach picks them again. */
export const planToPlanSeed = (plan: Plan, unit: UnitsPreference): PlanSeed => ({
  draft: { ...planToDraft(plan, unit).draft, assignedTo: [], assigneeEmails: [] },
  unit,
  source: `plan:${plan.id}`,
});
